import React, { useState, useEffect } from 'react';
import { Plus, Percent, DollarSign, Calendar, Users, Award, Trash2, Edit, Eye, Copy, Shuffle, Hash } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Badge } from '../ui/badge.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { Checkbox } from '../ui/checkbox.jsx';
import { toast } from 'sonner';
import axios from 'axios';
import { getErrorMessage } from '../../utils/errorUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [showBulkGenerateDialog, setShowBulkGenerateDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [bulkGenerateData, setBulkGenerateData] = useState({
    prefix: 'SAVE',
    count: 5,
    discount_type: 'percentage',
    discount_value: 10,
    coupon_type: 'public',
    target: 'subscription',
    usage_limit_per_user: 1,
    valid_until: null,
    minimum_purchase: null,
    new_customers_only: false
  });
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    coupon_type: 'public',
    target: 'subscription',
    usage_limit: null,
    usage_limit_per_user: 1,
    valid_from: new Date().toISOString().slice(0, 16),
    valid_until: null,
    minimum_purchase: null,
    applicable_tiers: [],
    new_customers_only: false,
    affiliate_partner: null,
    affiliate_commission: null
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      const response = await axios.get(`${API}/api/coupons`);
      setCoupons(response.data);
    } catch (error) {
      console.error('Error loading coupons:', error);
      toast.error(getErrorMessage(error, 'Failed to load coupons'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateCoupon = async () => {
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        usage_limit_per_user: parseInt(formData.usage_limit_per_user),
        minimum_purchase: formData.minimum_purchase ? parseFloat(formData.minimum_purchase) : null,
        affiliate_commission: formData.affiliate_commission ? parseFloat(formData.affiliate_commission) : null,
        valid_until: formData.valid_until || null
      };
      
      await axios.post(`${API}/api/coupons`, payload);
      toast.success('Coupon created successfully!');
      setShowCreateDialog(false);
      resetForm();
      loadCoupons();
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast.error(getErrorMessage(error, 'Failed to create coupon'));
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        usage_limit_per_user: parseInt(formData.usage_limit_per_user),
        minimum_purchase: formData.minimum_purchase ? parseFloat(formData.minimum_purchase) : null,
        affiliate_commission: formData.affiliate_commission ? parseFloat(formData.affiliate_commission) : null,
        valid_until: formData.valid_until || null
      };
      
      await axios.put(`${API}/api/coupons/${editingCoupon.id}`, payload);
      toast.success('Coupon updated successfully!');
      setShowEditDialog(false);
      setEditingCoupon(null);
      resetForm();
      loadCoupons();
    } catch (error) {
      console.error('Error updating coupon:', error);
      toast.error(getErrorMessage(error, 'Failed to update coupon'));
    }
  };

  const openEditDialog = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      name: coupon.name,
      description: coupon.description,
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      coupon_type: coupon.coupon_type,
      target: coupon.target,
      usage_limit: coupon.usage_limit,
      usage_limit_per_user: coupon.usage_limit_per_user,
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().slice(0, 16) : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : '',
      minimum_purchase: coupon.minimum_purchase,
      applicable_tiers: coupon.applicable_tiers || [],
      new_customers_only: coupon.new_customers_only,
      affiliate_partner: coupon.affiliate_partner,
      affiliate_commission: coupon.affiliate_commission
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      coupon_type: 'public',
      target: 'subscription',
      usage_limit: null,
      usage_limit_per_user: 1,
      valid_from: new Date().toISOString().slice(0, 16),
      valid_until: null,
      minimum_purchase: null,
      applicable_tiers: [],
      new_customers_only: false,
      affiliate_partner: null,
      affiliate_commission: null
    });
  };

  const viewCouponUsage = async (coupon) => {
    try {
      setSelectedCoupon(coupon);
      setShowUsageDialog(true);
      
      const response = await axios.get(`${API}/api/coupons/${coupon.id}/usage`);
      setUsageData(response.data);
    } catch (error) {
      console.error('Error loading coupon usage:', error);
      toast.error(getErrorMessage(error, 'Failed to load usage data'));
    }
  };

  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard!');
  };

  const generateRandomCoupons = async () => {
    try {
      const generatedCoupons = [];
      const promises = [];

      for (let i = 1; i <= parseInt(bulkGenerateData.count); i++) {
        const couponCode = `${bulkGenerateData.prefix}${i.toString().padStart(3, '0')}`;
        
        const couponPayload = {
          code: couponCode,
          name: `${bulkGenerateData.prefix} ${bulkGenerateData.discount_value}% Off - ${i}`,
          description: `Auto-generated coupon: ${bulkGenerateData.discount_value}% discount`,
          discount_type: bulkGenerateData.discount_type,
          discount_value: parseFloat(bulkGenerateData.discount_value),
          coupon_type: bulkGenerateData.coupon_type,
          target: bulkGenerateData.target,
          usage_limit_per_user: parseInt(bulkGenerateData.usage_limit_per_user),
          valid_until: bulkGenerateData.valid_until || null,
          minimum_purchase: bulkGenerateData.minimum_purchase ? parseFloat(bulkGenerateData.minimum_purchase) : null,
          new_customers_only: bulkGenerateData.new_customers_only
        };

        promises.push(
          axios.post(`${API}/api/coupons`, couponPayload).then(() => {
            generatedCoupons.push(couponCode);
          }).catch((error) => {
            console.error(`Error creating coupon ${couponCode}:`, error);
            // If coupon already exists, try with random suffix
            const randomSuffix = Math.random().toString(36).substr(2, 3).toUpperCase();
            const newCode = `${bulkGenerateData.prefix}${i.toString().padStart(3, '0')}${randomSuffix}`;
            const newPayload = { ...couponPayload, code: newCode, name: `${bulkGenerateData.prefix} ${bulkGenerateData.discount_value}% Off - ${newCode}` };
            
            return axios.post(`${API}/api/coupons`, newPayload).then(() => {
              generatedCoupons.push(newCode);
            });
          })
        );
      }

      await Promise.all(promises);
      
      toast.success(`Successfully generated ${generatedCoupons.length} coupons!`);
      setShowBulkGenerateDialog(false);
      setBulkGenerateData({
        prefix: 'SAVE',
        count: 5,
        discount_type: 'percentage',
        discount_value: 10,
        coupon_type: 'public',
        target: 'subscription',
        usage_limit_per_user: 1,
        valid_until: null,
        minimum_purchase: null,
        new_customers_only: false
      });
      loadCoupons();
      
    } catch (error) {
      console.error('Error generating coupons:', error);
      toast.error(getErrorMessage(error, 'Failed to generate coupons'));
    }
  };

  const handleBulkInputChange = (field, value) => {
    setBulkGenerateData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    return new Date(dateString).toLocaleDateString();
  };

  const getCouponTypeColor = (type) => {
    const colors = {
      public: 'bg-blue-100 text-blue-800',
      affiliate: 'bg-purple-100 text-purple-800',
      referral: 'bg-green-100 text-green-800',
      first_time: 'bg-yellow-100 text-yellow-800',
      loyalty: 'bg-indigo-100 text-indigo-800',
      seasonal: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      return (
        <div className="flex items-center gap-1">
          <Percent className="h-4 w-4" />
          {coupon.discount_value}% off
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1">
          <DollarSign className="h-4 w-4" />
          ${coupon.discount_value} off
        </div>
      );
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading coupons...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Coupon Management</h2>
          <p className="text-gray-600">Create and manage discount coupons and promotional codes</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkGenerateDialog(true)}>
            <Shuffle className="h-4 w-4 mr-2" />
            Bulk Generate
          </Button>
          
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Coupon
          </Button>
        </div>
      </div>

      {/* Create Coupon Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Coupon</DialogTitle>
              <DialogDescription>
                Set up a new discount coupon with specific rules and restrictions
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="code">Coupon Code *</Label>
                    <Input
                      id="code"
                      placeholder="e.g., SAVE20"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Display Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., 20% Off Summer Sale"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe the coupon and its benefits"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </div>
              </div>
              
              {/* Discount Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium">Discount Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select 
                      value={formData.discount_type} 
                      onValueChange={(value) => handleInputChange('discount_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="discount_value">
                      {formData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                    </Label>
                    <Input
                      id="discount_value"
                      type="number"
                      min="0"
                      max={formData.discount_type === 'percentage' ? "100" : undefined}
                      step={formData.discount_type === 'percentage' ? "1" : "0.01"}
                      value={formData.discount_value}
                      onChange={(e) => handleInputChange('discount_value', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Coupon Type & Targeting */}
              <div className="space-y-4">
                <h4 className="font-medium">Type & Targeting</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Coupon Type</Label>
                    <Select 
                      value={formData.coupon_type} 
                      onValueChange={(value) => handleInputChange('coupon_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="affiliate">Affiliate</SelectItem>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="first_time">First Time Customer</SelectItem>
                        <SelectItem value="loyalty">Loyalty Reward</SelectItem>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Target</Label>
                    <Select 
                      value={formData.target} 
                      onValueChange={(value) => handleInputChange('target', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="subscription">Subscriptions</SelectItem>
                        <SelectItem value="addon">Add-on Services</SelectItem>
                        <SelectItem value="all">All Purchases</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Usage Limits */}
              <div className="space-y-4">
                <h4 className="font-medium">Usage Limits</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="usage_limit">Total Usage Limit</Label>
                    <Input
                      id="usage_limit"
                      type="number"
                      min="1"
                      placeholder="Unlimited"
                      value={formData.usage_limit || ''}
                      onChange={(e) => handleInputChange('usage_limit', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="usage_limit_per_user">Per User Limit</Label>
                    <Input
                      id="usage_limit_per_user"
                      type="number"
                      min="1"
                      value={formData.usage_limit_per_user}
                      onChange={(e) => handleInputChange('usage_limit_per_user', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Validity Period */}
              <div className="space-y-4">
                <h4 className="font-medium">Validity Period</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valid_from">Valid From</Label>
                    <Input
                      id="valid_from"
                      type="datetime-local"
                      value={formData.valid_from}
                      onChange={(e) => handleInputChange('valid_from', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="valid_until">Valid Until</Label>
                    <Input
                      id="valid_until"
                      type="datetime-local"
                      value={formData.valid_until || ''}
                      onChange={(e) => handleInputChange('valid_until', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Restrictions */}
              <div className="space-y-4">
                <h4 className="font-medium">Restrictions</h4>
                <div>
                  <Label htmlFor="minimum_purchase">Minimum Purchase Amount ($)</Label>
                  <Input
                    id="minimum_purchase"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="No minimum"
                    value={formData.minimum_purchase || ''}
                    onChange={(e) => handleInputChange('minimum_purchase', e.target.value)}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="new_customers_only"
                    checked={formData.new_customers_only}
                    onCheckedChange={(checked) => handleInputChange('new_customers_only', checked)}
                  />
                  <Label htmlFor="new_customers_only">New customers only</Label>
                </div>
              </div>
              
              {/* Affiliate Information */}
              {formData.coupon_type === 'affiliate' && (
                <div className="space-y-4">
                  <h4 className="font-medium">Affiliate Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="affiliate_partner">Affiliate Partner</Label>
                      <Input
                        id="affiliate_partner"
                        placeholder="Partner name"
                        value={formData.affiliate_partner || ''}
                        onChange={(e) => handleInputChange('affiliate_partner', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="affiliate_commission">Commission (%)</Label>
                      <Input
                        id="affiliate_commission"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={formData.affiliate_commission || ''}
                        onChange={(e) => handleInputChange('affiliate_commission', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCoupon}>
                Create Coupon
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Edit Coupon Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Coupon</DialogTitle>
              <DialogDescription>
                Update coupon settings and restrictions
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="font-medium">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-code">Coupon Code *</Label>
                    <Input
                      id="edit-code"
                      placeholder="e.g., SAVE20"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                      disabled={true} // Don't allow editing code after creation
                      className="bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">Coupon code cannot be changed after creation</p>
                  </div>
                  <div>
                    <Label htmlFor="edit-name">Display Name *</Label>
                    <Input
                      id="edit-name"
                      placeholder="e.g., 20% Off Summer Sale"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    placeholder="Describe the coupon and its benefits"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                  />
                </div>
              </div>
              
              {/* Discount Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium">Discount Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select 
                      value={formData.discount_type} 
                      onValueChange={(value) => handleInputChange('discount_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-discount_value">
                      {formData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                    </Label>
                    <Input
                      id="edit-discount_value"
                      type="number"
                      min="0"
                      max={formData.discount_type === 'percentage' ? "100" : undefined}
                      step={formData.discount_type === 'percentage' ? "1" : "0.01"}
                      value={formData.discount_value}
                      onChange={(e) => handleInputChange('discount_value', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Usage Limits */}
              <div className="space-y-4">
                <h4 className="font-medium">Usage Limits</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-usage_limit">Total Usage Limit</Label>
                    <Input
                      id="edit-usage_limit"
                      type="number"
                      min="1"
                      placeholder="Unlimited"
                      value={formData.usage_limit || ''}
                      onChange={(e) => handleInputChange('usage_limit', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-usage_limit_per_user">Per User Limit</Label>
                    <Input
                      id="edit-usage_limit_per_user"
                      type="number"
                      min="1"
                      value={formData.usage_limit_per_user}
                      onChange={(e) => handleInputChange('usage_limit_per_user', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Validity Period */}
              <div className="space-y-4">
                <h4 className="font-medium">Validity Period</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-valid_from">Valid From</Label>
                    <Input
                      id="edit-valid_from"
                      type="datetime-local"
                      value={formData.valid_from}
                      onChange={(e) => handleInputChange('valid_from', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="edit-valid_until">Valid Until</Label>
                    <Input
                      id="edit-valid_until"
                      type="datetime-local"
                      value={formData.valid_until || ''}
                      onChange={(e) => handleInputChange('valid_until', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Restrictions */}
              <div className="space-y-4">
                <h4 className="font-medium">Restrictions</h4>
                <div>
                  <Label htmlFor="edit-minimum_purchase">Minimum Purchase Amount ($)</Label>
                  <Input
                    id="edit-minimum_purchase"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="No minimum"
                    value={formData.minimum_purchase || ''}
                    onChange={(e) => handleInputChange('minimum_purchase', e.target.value)}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-new_customers_only"
                    checked={formData.new_customers_only}
                    onCheckedChange={(checked) => handleInputChange('new_customers_only', checked)}
                  />
                  <Label htmlFor="edit-new_customers_only">New customers only</Label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowEditDialog(false);
                setEditingCoupon(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button onClick={handleUpdateCoupon}>
                Update Coupon
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog open={showBulkGenerateDialog} onOpenChange={setShowBulkGenerateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shuffle className="h-5 w-5" />
                Bulk Generate Coupons
              </DialogTitle>
              <DialogDescription>
                Generate multiple coupons automatically with sequential codes
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-6 py-4">
              {/* Generation Settings */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bulk-prefix">Code Prefix</Label>
                    <Input
                      id="bulk-prefix"
                      placeholder="e.g., SAVE, DEAL"
                      value={bulkGenerateData.prefix}
                      onChange={(e) => handleBulkInputChange('prefix', e.target.value.toUpperCase())}
                    />
                    <p className="text-xs text-gray-500 mt-1">Will generate: {bulkGenerateData.prefix}001, {bulkGenerateData.prefix}002...</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="bulk-count">Number of Coupons</Label>
                    <Input
                      id="bulk-count"
                      type="number"
                      min="1"
                      max="100"
                      value={bulkGenerateData.count}
                      onChange={(e) => handleBulkInputChange('count', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Discount Configuration */}
              <div className="space-y-4">
                <h4 className="font-medium">Discount Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select 
                      value={bulkGenerateData.discount_type} 
                      onValueChange={(value) => handleBulkInputChange('discount_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="bulk-discount_value">
                      {bulkGenerateData.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                    </Label>
                    <Input
                      id="bulk-discount_value"
                      type="number"
                      min="0"
                      max={bulkGenerateData.discount_type === 'percentage' ? "100" : undefined}
                      step={bulkGenerateData.discount_type === 'percentage' ? "1" : "0.01"}
                      value={bulkGenerateData.discount_value}
                      onChange={(e) => handleBulkInputChange('discount_value', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              {/* Quick Settings */}
              <div className="space-y-4">
                <h4 className="font-medium">Quick Settings</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Coupon Type</Label>
                    <Select 
                      value={bulkGenerateData.coupon_type} 
                      onValueChange={(value) => handleBulkInputChange('coupon_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="affiliate">Affiliate</SelectItem>
                        <SelectItem value="seasonal">Seasonal</SelectItem>
                        <SelectItem value="loyalty">Loyalty</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="bulk-usage_limit_per_user">Uses Per User</Label>
                    <Input
                      id="bulk-usage_limit_per_user"
                      type="number"
                      min="1"
                      value={bulkGenerateData.usage_limit_per_user}
                      onChange={(e) => handleBulkInputChange('usage_limit_per_user', e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="bulk-valid_until">Expiration Date (Optional)</Label>
                  <Input
                    id="bulk-valid_until"
                    type="datetime-local"
                    value={bulkGenerateData.valid_until || ''}
                    onChange={(e) => handleBulkInputChange('valid_until', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor="bulk-minimum_purchase">Minimum Purchase ($)</Label>
                  <Input
                    id="bulk-minimum_purchase"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="No minimum"
                    value={bulkGenerateData.minimum_purchase || ''}
                    onChange={(e) => handleBulkInputChange('minimum_purchase', e.target.value)}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bulk-new_customers_only"
                    checked={bulkGenerateData.new_customers_only}
                    onCheckedChange={(checked) => handleBulkInputChange('new_customers_only', checked)}
                  />
                  <Label htmlFor="bulk-new_customers_only">New customers only</Label>
                </div>
              </div>
              
              {/* Preview */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <h5 className="font-medium text-sm mb-2">Preview:</h5>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>• Will create {bulkGenerateData.count} coupons</div>
                  <div>• Codes: {bulkGenerateData.prefix}001 to {bulkGenerateData.prefix}{bulkGenerateData.count.toString().padStart(3, '0')}</div>
                  <div>• Discount: {bulkGenerateData.discount_value}{bulkGenerateData.discount_type === 'percentage' ? '%' : '$'} off</div>
                  <div>• Type: {bulkGenerateData.coupon_type}</div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkGenerateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={generateRandomCoupons}>
                <Hash className="h-4 w-4 mr-2" />
                Generate {bulkGenerateData.count} Coupons
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Coupons List */}
      <div className="grid gap-4">
        {coupons.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No coupons created yet</h3>
              <p className="text-gray-600 mb-4">Create your first discount coupon to start offering promotions</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Coupon
              </Button>
            </CardContent>
          </Card>
        ) : (
          coupons.map((coupon) => (
            <Card key={coupon.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="px-3 py-1 bg-gray-100 rounded-lg font-mono text-lg font-bold">
                        {coupon.code}
                      </code>
                      <Badge className={getCouponTypeColor(coupon.coupon_type)}>
                        {coupon.coupon_type}
                      </Badge>
                      {!coupon.is_active && (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 mb-1">{coupon.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{coupon.description}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Discount:</span>
                        <div className="font-medium text-green-600">
                          {getDiscountDisplay(coupon)}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Usage:</span>
                        <div className="font-medium">
                          {coupon.current_usage}
                          {coupon.usage_limit && `/${coupon.usage_limit}`}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Valid Until:</span>
                        <div className="font-medium">{formatDate(coupon.valid_until)}</div>
                      </div>
                      
                      <div>
                        <span className="text-gray-500">Created:</span>
                        <div className="font-medium">{formatDate(coupon.created_at)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyCouponCode(coupon.code)}
                      title="Copy coupon code"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(coupon)}
                      title="Edit coupon"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewCouponUsage(coupon)}
                      title="View usage analytics"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Usage Analytics Dialog */}
      <Dialog open={showUsageDialog} onOpenChange={setShowUsageDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Coupon Usage Analytics</DialogTitle>
            <DialogDescription>
              {selectedCoupon && `Analytics for coupon: ${selectedCoupon.code}`}
            </DialogDescription>
          </DialogHeader>
          
          {usageData && (
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{usageData.statistics.total_usage}</div>
                    <div className="text-sm text-gray-600">Total Uses</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">${usageData.statistics.total_discount_given?.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Total Savings</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">${usageData.statistics.total_revenue_impact?.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Revenue Impact</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">${usageData.statistics.average_discount?.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Avg Discount</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Recent Usage */}
              {usageData.recent_usage.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Recent Usage</h4>
                  <div className="space-y-2">
                    {usageData.recent_usage.map((usage) => (
                      <div key={usage.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium">{usage.user_email}</div>
                          <div className="text-sm text-gray-600">
                            {formatDate(usage.used_at)} • {usage.subscription_tier}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">${usage.final_amount}</div>
                          <div className="text-sm text-green-600">-${usage.discount_amount}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponManager;